// ScientificDivideTask.js
// Dzielenie: (a1·10^k1) / (a2·10^k2). Mantysy 0/1/2 miejsca po przecinku (ostatnia ≠ 0).
// Gwarantujemy, że m1/m2 ma skończone rozwinięcie dziesiętne (po skróceniu mianownik z {2,5}).
// Stabilizacja FP po normalizacji (eliminuje np. 1.4749999999999999).

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

const int=(a,b)=>a+Math.floor(Math.random()*(b-a+1));
const pick=(arr)=>arr[Math.floor(Math.random()*arr.length)];

function randMantissa(){
  const dec=pick([0,1,2]);
  if(dec===0){const v=int(1,9);return{value:v,str:String(v)};}
  if(dec===1){const a=int(1,9), b=int(1,9); const v=parseFloat(`${a}.${b}`); return{value:v,str:v.toFixed(1)};}
  const a=int(1,9), b=int(0,9), c=int(1,9); const v=parseFloat(`${a}.${b}${c}`); return{value:v,str:v.toFixed(2)};}
function randNonZeroExp(){return pick([-3,-2,-1,1,2,3]);}

const fmt=(x)=>String(x).replace(".",",");
const canonSci=(aStr,k)=>`${aStr.replace(".",",")}\\,\\cdot\\,10^{${k}}`;

function sig5_noRound(m){
  const s=m.toString();
  const digits=s.replace(".","");
  const take=Math.min(5,digits.length);
  const first=digits[0];
  const rest=digits.slice(1,take);
  let out=first+(rest? "."+rest : "");
  out=out.replace(/0+$/,"").replace(/\.$/,"");
  return { str: out, num: Number(out) };
}

// --------- narzędzia do "skończonych dziesiętnie" i dokładnego dzielenia ---------

function strToIntAndPow10(str){
  const s = String(str);
  if (!s.includes(".")) return { N: parseInt(s,10), d: 0 };
  const [I,F] = s.split(".");
  const d = F.length;
  const N = parseInt(I + F, 10);
  return { N, d };
}
function gcd(a,b){ a=Math.abs(a); b=Math.abs(b);
  while(b){ const t=a%b; a=b; b=t; }
  return a;
}
function only2and5(n){
  let x = Math.abs(n);
  while (x % 2 === 0) x /= 2;
  while (x % 5 === 0) x /= 5;
  return x === 1;
}
function divisionHasTerminatingDecimal(m1str, m2str){
  const {N:N1, d:d1} = strToIntAndPow10(m1str);
  const {N:N2, d:d2} = strToIntAndPow10(m2str);
  const num = N1 * (10 ** d2);
  const den = N2 * (10 ** d1);
  const g = gcd(num, den);
  const denReduced = den / g;
  return only2and5(denReduced);
}

/** Zwraca DOKŁADNY string dziesiętny dla m1/m2 (wiemy, że jest skończony). */
function exactDecimalOfDivision(m1str, m2str){
  const {N:N1, d:d1} = strToIntAndPow10(m1str);
  const {N:N2, d:d2} = strToIntAndPow10(m2str);
  let num = N1 * (10 ** d2);
  let den = N2 * (10 ** d1);
  const g = gcd(num, den);
  num /= g; den /= g;
  // den = 2^a * 5^b
  let a = 0, b = 0, t = den;
  while (t % 2 === 0) { a++; t/=2; }
  while (t % 5 === 0) { b++; t/=5; }
  const T = Math.max(a,b);
  if (a > b) { // pomnóż licznik przez 5^(a-b)
    num *= 5 ** (a-b);
  } else if (b > a) { // pomnóż licznik przez 2^(b-a)
    num *= 2 ** (b-a);
  }
  // teraz mianownik = 10^T
  const s = String(num);
  if (T === 0) return s.replace(".",",");
  if (s.length <= T) {
    return ("0," + "0".repeat(T - s.length) + s).replace(/\.$/,",");
  }
  const before = s.slice(0, s.length - T);
  const after  = s.slice(s.length - T);
  return (before + "," + after).replace(/,0+$/,"").replace(/,$/,"");
}

// --------- format "Twoja odpowiedź" ---------

function prettySciLatex(raw) {
  if (!raw) return "";
  const s = String(raw).trim().replace(/\s/g, "");
  let m = s.match(/^(\d+(?:[.,]\d+)?)(?:\*|·)?10\^\(?([+\-]?\d+)\)?$/i);
  if (m) return `${m[1].replace(".", ",")}\\,\\cdot\\,10^{${m[2]}}`;
  m = s.match(/^(\d+(?:[.,]\d+)?)[eE]([+\-]?\d+)$/);
  if (m) return `${m[1].replace(".", ",")}\\,\\cdot\\,10^{${m[2]}}`;
  return s.replace(/\^-(\d+)/g, "^{-$1}").replace(".", ",");
}

export function generateTask(){
  // Losuj tak długo, aż m1/m2 ma skończone rozwinięcie dziesiętne
  let m1, m2;
  let tries = 0;
  do {
    m1 = randMantissa();
    m2 = randMantissa();
    tries++;
    if (tries > 100) break;
  } while (!divisionHasTerminatingDecimal(m1.str, m2.str));

  const k1=randNonZeroExp(), k2=randNonZeroExp();

  const leftLatex=`\\dfrac{${canonSci(m1.str,k1)}}{${canonSci(m2.str,k2)}}`;

  // --- obliczenia mantysy i wykładnika ---
  let mantissa = m1.value / m2.value;
  let k = k1 - k2;

  // normalizacja do [1,10)
  while(mantissa>=10){mantissa/=10;k+=1;}
  while(mantissa<1){mantissa*=10;k-=1;}

  // stabilizacja FP (12 miejsc)
  mantissa = Math.round(mantissa * 1e12) / 1e12;
  if (mantissa >= 10) { mantissa /= 10; k += 1; }
  if (mantissa < 1)   { mantissa *= 10; k -= 1; }

  const { str: mantissaStr, num: mantissaNum } = sig5_noRound(mantissa);

  return {
    type:"ScientificDivideTask",
    data:{
      leftLatex, m1, k1, m2, k2,
      mantissa: mantissaNum, mantissaStr, k
    }
  };
}

export function renderTask({data}){
  return <div style={{marginBottom:"12px"}}>
    Oblicz wynik i zapisz w <strong>notacji wykładniczej</strong>:{" "}
    <InlineMath math={data.leftLatex}/>
  </div>;
}

export function renderInput(task,user,onChange,disabled){
  const handleChange=(ans)=>onChange?.(ans);
  const {mantissaStr,k}=task.data;
  const ph=`np. 7,7·10^7 lub 7,7e7`;
  return (
    <div>
      <FractionInput
        value={user}
        onChange={handleChange}
        disabled={disabled}
        placeholder={ph}
        label=""
        powerPreview={false}
        sciPreview={true}
      />
      <div aria-hidden style={{ marginTop: "-16px" }} />
      <div aria-hidden style={{ height: "12px" }} />
    </div>
  );
}

export function checkAnswer({data},userAnswer){
  if(!userAnswer?.raw) return false;
  const s=userAnswer.raw.replace(",",".").replace(/\s/g,"");
  const m=s.match(/^(\d+(?:\.\d+)?)\*?·?10\^(-?\d+)$/);
  if(!m) return false;
  const a=Number(m[1]), k=parseInt(m[2],10);
  if (!(a>=1 && a<10) || k!==data.k) return false;
  return Math.abs(a - data.mantissa) < 1e-10;
}

export function renderUserAnswer(ans){
  if(!ans?.raw)return<i>(brak odpowiedzi)</i>;
  return <InlineMath math={prettySciLatex(ans.raw)} />;
}

export function renderAnswer({data}){
  return <InlineMath math={`${fmt(data.mantissaStr)}\\,\\cdot\\,10^{${data.k}}`} />;
}

export function renderExplanation({ data }) {
  const { m1, m2, k1, k2, mantissaStr, k } = data;

  const m1s = m1.str.replace(".", ","), m2s = m2.str.replace(".", ",");
  const expDiffLatex = k2 < 0 ? `${k1}-(${k2})` : `${k1}-${k2}`;
  const exactDec = exactDecimalOfDivision(m1.str, m2.str); // np. "1,475"

  return (
    <div style={{ display: "grid", rowGap: "10px" }}>
      <p><strong>Krok 1:</strong> Zapisz działanie:</p>
      <BlockMath math={`\\dfrac{${m1s}\\,\\cdot\\,10^{${k1}}}{${m2s}\\,\\cdot\\,10^{${k2}}}`} />

      <p><strong>Krok 2:</strong> Podziel mantysy i odejmij wykładniki potęg dziesięciu:</p>
      <BlockMath math={`\\dfrac{${m1s}}{${m2s}}\\;\\cdot\\;10^{${expDiffLatex}}`} />
      <BlockMath math={`=\\;${exactDec}\\,\\cdot\\,10^{${k1-k2}}`} />

      <p><strong>Krok 3:</strong> Znormalizuj mantysę do <InlineMath math={'[1,10)'} /> i zapisz wynik w notacji wykładniczej:</p>
      <BlockMath math={`${fmt(mantissaStr)}\\,\\cdot\\,10^{${k}}`} />
    </div>
  );
}
